<script>
    import _ from 'lodash'
    import moment from 'moment'
    import { appComputed } from '@state/helpers'
    import CONSTANTS from '../../constants'
    import Helper from '../../utils/helper'
    import CoachingPreview from '../../components/coaching-preview.vue'
    import UserSelection from '@components/user-selection'
    import OrganizationSelection from '@components/organization-selection'
    import TemplateSelection from '@components/template-selection'

    export default {
        components: {
            TemplateSelection,
            OrganizationSelection,
            UserSelection,
            CoachingPreview
        },
        props: {
            value: {
                type: Boolean,
                default: false
            },
            coachingUnitId: {
                type: String,
                default: null
            }
        },
        data() {
            return {
                coachingUser: [],
                template: null,
                tpls: [],
                crows: [],
                tabs: null,
                processing: false,
                filterUser: '',
                closeWindow: false,
                configParams: null,
                selectedOrg: [],
                showOrgSel: false,
                showOrgSelDialog: false,
                selectedTemplate: []
            }
        },

        computed: {
            ...appComputed,
            organisation() {
                return this.$store.getters['api/organisation']
            },
            organisationUsersActive() {
                return this.$store.getters['api/organisationUsersActive']
            },
            writeableOrganizations() {
                return this.$store.getters['api/organisations']
                    .filter(org => CONSTANTS.GROUPS_EDIT.includes(org.organizationGroup))
            },
            templates() {
                return this.$store.getters['api/templates']
            },
            header() {
                return this.$vuetify.lang.t('$vuetify.coaching.planning')
            },
            color() {
                return CONSTANTS.MAIN_BACKGROUND
            },
            dataIsValid() {
                // TODO add !_.isEmpty(this.selectedOrg)?
                return !_.isEmpty(this.coachingUser) && !_.isEmpty(this.selectedTemplate) && !_.isEmpty(this.selectedOrg)
            },
            dialogWidth() {
                return Helper.dialogWidth(this.$vuetify.breakpoint.name)
            },
            disabledUserSelection() {
                if (this.template) return true
                if (this.showOrgSel && !this.selectedOrg) return true
                return !this.organisationUsersActive.length
            },
            // show button for "create and edit" only when a single user and template is selected
            enableEditButton() {
                return _.size(this.coachingUser) === 1 && _.size(this.selectedTemplate) === 1
            }
        },
        watch: {
            // clear template when dialog reopens or closed
            value() {
                this.clear()
                this.$nextTick(() => {
                    this.clearRows()
                    this.checkSingleUser()
                })
            },
            templates(tpls) {
                this.tpls = tpls
            },
            organisationUsersActive() {
                this.preSelectUser()
            }
        },

        created() {
            this.parseQuery()
        },
        mounted() {
            // selectedOrg must be set to skip organisation selection
            // preselect current organisation
            this.selectedOrg = [this.organisation]
            this.$autofocus()
            this.$nextTick(() => {
                this.clearRows()
                this.preSelectUser()
                this.checkSingleUser()
            })
        },

        methods: {
            parseQuery() {
                // preselection only with create parameter
                if (!this.$route.query.create) return

                const payload = this.$json.parse(window.atob(this.$route.query.payload || ''))
                if (_.isEmpty(payload)) return

                this.closeWindow = true
                this.configParams = payload
                this.showOrgSel = true
            },

            // TODO make this real async (return Promise and Promise.all)
            async _create() {
                return new Promise((resolve, reject) => {
                    if (!this.dataIsValid) return reject()
                    const organisation = this.selectedOrg[0]
                    if (_.isEmpty(organisation)) return reject() // should not happen
                    if (_.isEmpty(this.selectedTemplate)) return reject() // should not happen
                    if (_.isEmpty(this.coachingUser)) return reject() // should not happen

                    const employees = this.coachingUser.map(user => _.pick(user, ['userId', 'username']))
                    const organisationId = organisation.organisationId
                    const isFinished = false

                    const promises = []
                    let newCoachings = 0
                    _.each(this.selectedTemplate, template => {
                        const config = Helper.templateToCoaching.call(this, template, _.omit(this.configParams, ['employee']))
                        // NOTE we set coachingDate to empty string as flag, that the coaching is untouched
                        const coachingData = JSON.stringify({config, coachingDate: ''})
                        const payload = {name: template.name, organisationId, coachingData, isFinished}
                        if (this.coachingUnitId) payload.coachingUnitId = this.coachingUnitId
                        employees.forEach(user => {
                            promises.push(
                                this.$store.dispatch('api/COACHING_CREATE', {userId: user.userId, ...payload})
                            )
                            newCoachings++
                        })
                    })

                    // TODO what to clear or use this.clear()?
                    this.coachingUser = []

                    Promise.all(promises)
                        .then(createdCoachings => {
                            resolve(createdCoachings)
                        })
                        .catch(reject)
                })
            },
            async createCoachings() {
                const newCoachings = await this._create()
                if (!newCoachings) return

                this.coachingCreatedMessage(_.size(newCoachings))

                _.delay(() => {this.close()}, this.closeWindow ? 4000 : 0)
            },
            async createAndEditCoaching() {
                const newCoachings = await this._create()
                if (!_.size(newCoachings)) return

                this.coachingCreatedMessage(_.size(newCoachings))

                this.$emit('edit', newCoachings[0])
            },
            coachingCreatedMessage(newCoachingsCount) {
                this.$toast.open({
                    type: 'success',
                    title: this.$vuetify.lang.t('$vuetify.coaching.planning'),
                    text: this.$vuetify.lang.t(`$vuetify.coaching.newText${newCoachingsCount === 1 ? 'One' : ''}`)
                        .replace('<% len %>', `${newCoachingsCount}`),
                    timeout: 8000,
                    closeable: true
                })
            },

            addTemplate(template) {
                if (_.isEmpty(template)) return
                if (_.size(template) > 1) return this.createCoachings() // no preview for more than one selected template

                // set data to show coaching preview
                this.template = template[0]
                this.crows = Helper.templateToCoaching.call(this, template[0], _.omit(this.configParams, ['employee']))
            },
            onTemplateSelection(template) {
                this.selectedTemplate = template
            },
            clearRows() {
                const gf = this.$gridform['coachingPreview']
                if (gf) gf.clear()
            },
            onUserSelection(users) {
                this.coachingUser = users
            },

            onOrganizationSelection(org) {
                if (_.isEmpty(org)) return

                this.selectedOrg = org
                const organisation = org[0]
                if (organisation?.organisationId === this.organisation.organisationId) return
                if (organisation) {
                    // clear current selections
                    this.coachingUser = []
                    this.selectedTemplate = []
                    // make selected org the active one
                    // -> reloads users and templates and refresh selections
                    this.$store.dispatch('api/ORG_ACTIVE', organisation)
                }
            },
            closeOrgSelect() {
                this.showOrgSel = false
                this.close()
            },
            preSelectUser() {
                const payload = this.configParams
                if (_.isEmpty(payload)) return

                const employee = _.isArray(payload.employee) ? payload.employee : [payload.employee]
                if (!_.isEmpty(employee)) {
                    // TODO add all not found user to filter?
                    // TODO support multiple filter separated by comma? Agent1,Agent2
                    // this.filterUser = user.toString()
                    const selected = _.filter(this.organisationUsersActive, oau => employee.includes(oau.username))
                    if (selected.length) {
                        this.coachingUser = selected
                        this.$autofocus('.vc-template-selection__filter')
                        return
                    }
                }

                // set focus to filter input in user selection
                this.$autofocus('.vc-user-selection__filter')
            },
            checkSingleUser() {
                if (_.isEmpty(this.coachingUser) && _.size(this.organisationUsersActive) === 1) {
                    this.coachingUser = this.organisationUsersActive
                    this.$autofocus('.vc-template-selection__filter')
                }
            },

            domToImagePreview() {
                const filename = this.$vuetify.lang.t('$vuetify.coaching.imageFilenamePlanning').replace('{date}', moment().format('YYYYMMDD-HHmm'))
                this.$domToImage(this.$refs.preview.$el.querySelector('.vgf__content'), filename)
            },
            pdfPreview() {
                this.processing = true

                // expand all fieldlists
                const gf = this.$gridform['coachingPreview']
                gf.config({disabled: false})
                // TODO fieldlist option to collapse all fieldlists
                const fls = gf.fieldlist().map(fl => fl.fieldlist.id)
                fls.forEach(flid => {
                    gf.fieldlist({id: flid, collapsed: false})
                })

                this.$nextTick(() => {
                    const {logo, footer} = Helper.pdfInit()
                    const title = `${this.template.initials} - ${this.template.name}`
                    const subtitle = this.template.description
                    const nodes = this.$refs.preview.$el.querySelectorAll('.vgf__fieldlist')
                    const filename = this.$vuetify.lang.t('$vuetify.coaching.pdfFilenamePlanning').replace('{date}', moment().format('YYYYMMDD-HHmm'))

                    this.$domToPDF(nodes, {title, subtitle, logo, footer, open: false}, filename)
                        .catch(Helper.errorHandler)
                        .finally(() => {
                            gf.config({disabled: true})
                            fls.forEach(flid => {
                                gf.fieldlist({id: flid, collapsed: true})
                            })
                            this.processing = false
                        })
                })
            },

            clear() {
                this.template = null
                this.selectedTemplate = []
                this.coachingUser = []
                this.crows = []
            },
            close() {
                if (this.closeWindow) return window.close()
                this.$emit('close')
            }
        }
    }
</script>

<template>
    <VSheet class="coaching">
        <VToolbar :dense="dense" :color="color" :light="$vuetify.theme.dark" :dark="!$vuetify.theme.dark" :elevation="4">
            <VToolbarTitle>{{ header }}</VToolbarTitle>
            <VSpacer/>
            <VTooltip :disabled="!$vuetify.breakpoint.mdAndDown" bottom>
                <template v-slot:activator="{ on }">
                    <VBtn text outlined :small="$vuetify.breakpoint.smAndDown" class="ml-2" :disabled="!dataIsValid || !enableEditButton" v-on="on" @click.left="createAndEditCoaching">
                        <VIcon>mdi-pencil</VIcon>
                        <span v-if="!$vuetify.breakpoint.mdAndDown" class="ml-1">
                          {{ $vuetify.lang.t('$vuetify.actions.edit') }}
                        </span>
                    </VBtn>
                </template>
                <span>{{ $vuetify.lang.t('$vuetify.actions.edit') }}</span>
            </VTooltip>
            <VTooltip :disabled="!$vuetify.breakpoint.mdAndDown" bottom>
                <template v-slot:activator="{ on }">
                    <VBtn text outlined :small="$vuetify.breakpoint.smAndDown" class="ml-2" :disabled="!dataIsValid" v-on="on" @click.left="createCoachings">
                        <VIcon>mdi-content-save-outline</VIcon>
                        <span v-if="!$vuetify.breakpoint.mdAndDown" class="ml-1">
                          {{ $vuetify.lang.t('$vuetify.actions.create') }}
                        </span>
                    </VBtn>
                </template>
                <span>{{ $vuetify.lang.t('$vuetify.actions.create') }}</span>
            </VTooltip>
            <VTooltip :disabled="!$vuetify.breakpoint.mdAndDown" bottom>
                <template v-slot:activator="{ on }">
                    <VBtn text outlined :small="$vuetify.breakpoint.smAndDown" class="ml-2" v-on="on" @click.left="close">
                        <VIcon>mdi-exit-to-app</VIcon>
                        <span v-if="!$vuetify.breakpoint.mdAndDown" class="ml-1">
                          {{ $vuetify.lang.t('$vuetify.actions.' + (closeWindow ? 'close' : 'cancel')) }}
                        </span>
                    </VBtn>
                </template>
                <span>{{ $vuetify.lang.t('$vuetify.actions.' + (closeWindow ? 'close' : 'cancel')) }}</span>
            </VTooltip>
        </VToolbar>

        <VLayout class="coaching__layout justify-center">
            <VFlex xs12 sm12 md10 lg10>
                <VContainer fluid class="tselection">
                    <VRow>
                        <!-- Organization -->
                        <VCol v-if="showOrgSel" cols="12">
                            <OrganizationSelection
                                v-model="selectedOrg"
                                :items="!template ? writeableOrganizations : selectedOrg"
                                :title="$vuetify.lang.t('$vuetify.organisation.selection.titleSingle')"
                                :hint="$vuetify.lang.t('$vuetify.organisation.selection.createHint')"
                                :disabled="!!template"
                                single-select
                                no-actions
                                @change="onOrganizationSelection"
                            />
                        </VCol>

                        <!-- User selection -->
                        <VCol v-if="selectedOrg.length" cols="12">
                            <UserSelection
                                v-model="coachingUser"
                                :items="!template ? organisationUsersActive : coachingUser"
                                :title="$vuetify.lang.t('$vuetify.coaching.selectEmployee')"
                                :hint="$vuetify.lang.t('$vuetify.coaching.hintEmployee')"
                                :disabled="disabledUserSelection"
                                :filter="filterUser"
                                @change="onUserSelection"
                            />
                        </VCol>

                        <!-- Template selection -->
                        <VCol v-if="!template && selectedOrg.length" cols="12">
                            <TemplateSelection
                                v-model="selectedTemplate"
                                :items="templates"
                                :title="$vuetify.lang.t('$vuetify.coaching.selectTemplate')"
                                :hint="$vuetify.lang.t('$vuetify.coaching.hintTemplate')"
                                :label-submit="selectedTemplate.length > 1 ? $vuetify.lang.t('$vuetify.actions.create') : ''"
                                submit-icon="mdi-content-save-outline"
                                :single-select="false"
                                :disabled="!coachingUser.length"
                                no-cancel
                                @change="onTemplateSelection"
                                @submit="addTemplate"
                            />
                        </VCol>

                        <!-- Coaching preview -->
                        <VCol v-if="template" cols="12">
                            <VCard elevation="0">
                                <VToolbar v-if="template" color="primary" :dark="!$vuetify.theme.dark" :light="$vuetify.theme.dark" flat :dense="dense">
                                    <VToolbarTitle>{{ template.description }} ({{ $vuetify.lang.t('$vuetify.coaching.preview') }})</VToolbarTitle>
                                    <VSpacer/>
                                    <VBtn icon :loading="processing" @click.left="pdfPreview">
                                        <VIcon>mdi-file-pdf-box</VIcon>
                                    </VBtn>
                                    <VBtn icon @click.left="domToImagePreview">
                                        <VIcon>mdi-camera</VIcon>
                                    </VBtn>
                                    <VBtn icon @click.left="clear">
                                        <VIcon>mdi-delete-circle-outline</VIcon>
                                    </VBtn>
                                </VToolbar>
                                <CoachingPreview ref="preview" :rows="crows" @submit="createCoachings" @cancel="clear"/>
                            </VCard>
                        </VCol>
                    </VRow>
                </VContainer>
            </VFlex>
        </VLayout>

        <VDialog v-model="showOrgSelDialog" :max-width="dialogWidth">
            <OrganizationSelection
                :title="$vuetify.lang.t('$vuetify.organisation.selection.titleSingle')"
                :hint="$vuetify.lang.t('$vuetify.organisation.selection.createHint')"
                :items="writeableOrganizations"
                single-select
                :label-submit="$vuetify.lang.t('$vuetify.actions.apply')"
                @submit="onOrganizationSelection"
                @close="closeOrgSelect"
            />
        </VDialog>
    </VSheet>
</template>

<style lang="scss" scoped>
    .coaching {
        position: fixed;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        z-index: 99;
        transition: opacity 1s ease-in 1s;
    }
    .coaching__layout {
        height: calc(100% - 48px);
        overflow: auto;
    }
    .tselection {
        margin-bottom: 48px;
    }
</style>
